import React from "react";
import { Trans } from "@lingui/macro";
import {
  SectionContainer,
  SectionTitleBody,
  SectionTitleHeading
} from "@silversea-ssc/ui-components-react";
import { SectionTitle } from "@silversea-ssc/ui-components-react";
import { SpecialVoyageQuery } from "types/graphql-types";
import { sortSuites } from "../../utils/cruise";
import { MosaicLayout } from "@silversea-ssc/ui-components-react";
import CruiseSuiteCard from "components/CruiseSuiteCard/CruiseSuiteCard";

import { SanityBlock } from "types/graphql-types";
import { useGeolocation } from "../../utils/localization/geolocation/GeolocationContext";
import CruiseFareInclusionsList from "components/CruiseFareInclusionsList/CruiseFareInclusionsList";
import { CruisePrice } from "types/api/suite_price.d";
import { BlockContent } from "components/BlockContent";
import { CruiseSuiteSetOpenModalFn } from "../Cruise/Cruise";
import { priceForSuite } from "src/utils/suites";
import { LocationSpecificOffer } from "components/useLocationSpecificOffers/types";
import { getSuiteSpecificRAQ } from "src/utils/requestAQuoteHelper";
import { useLanguage } from "../../utils/localization/languages/I18nContext";
import { isNullOrUndefined, isPresent } from "src/utils/checks";

export interface CruiseSectionSuitesProps {
  cruise: SpecialVoyageQuery["cruise"];
  cruisePrice: CruisePrice | undefined;
  offers: LocationSpecificOffer[];
  setOpenModal: CruiseSuiteSetOpenModalFn;
  cruiseFareInclusions: SanityBlock[];
  cruiseFareInclusionsFootnotes: SanityBlock[];
}

const CruiseSectionSuites: React.FC<CruiseSectionSuitesProps> = ({
  cruise,
  setOpenModal,
  cruisePrice,
  offers,
  cruiseFareInclusions,
  cruiseFareInclusionsFootnotes
}) => {
  const cruiseData = cruise?.data;
  const ship = cruiseData?.ship;

  const geoLocation = useGeolocation();
  const language = useLanguage();

  if (
    isNullOrUndefined(cruise) ||
    isNullOrUndefined(cruiseData) ||
    isNullOrUndefined(ship)
  )
    return null;

  const suiteDescriptionKey =
    geoLocation.unit == "feet"
      ? "shortSizeDescriptionInSquareFeet"
      : "shortSizeDescriptionInSquareMetres";

  const sortedSuites = sortSuites((ship.suites || []).filter(isPresent));

  return (
    <SectionContainer
      spacingTop="regular"
      spacingBottom="regular"
      backgroundColor="destinationLight"
      dataContentName="section_suites"
    >
      <SectionTitle variant="wide">
        <SectionTitleHeading>
          <Trans>Suites & Fares</Trans>
        </SectionTitleHeading>
        <SectionTitleBody>
          <p>
            <Trans id="sp-wc-template-suites-fares-body">
              Broad decks, panoramic lounges, gourmet dining options and
              all-suite accommodations ensure the most comfortable journey.
              Along with the incomparable luxury that has made Silversea the
              preferred choice for today’s discerning travellers.
            </Trans>

            {cruiseData.comboType == "Grand" && (
              <i style={{ fontStyle: "italic" }}>
                <br />
                <Trans id="special-voyage-template-suites-fares-disclaimer">
                  All prices shown are double occupancy based and discounted
                  with the Combo Cruise benefit (-5%).
                </Trans>
              </i>
            )}
          </p>
        </SectionTitleBody>
      </SectionTitle>

      <MosaicLayout variant="column3">
        {sortedSuites.map((suite) => {
          const suitePrice = priceForSuite(
            cruisePrice?.suitePrices,
            suite.codes
          );
          if (!suitePrice) return null;
          const raqUrl = getSuiteSpecificRAQ(
            language.code,
            cruiseData.cruiseCode,
            ship._id,
            suitePrice.suiteCategory
          );
          const suiteDescription = suite[suiteDescriptionKey]?.localized;

          return (
            <CruiseSuiteCard
              key={suite._key}
              title={suite?.card?.title?.localized}
              description={suiteDescription}
              image={suite?.card?.image}
              price={suitePrice.priceFrom}
              offers={offers}
              raqCTAUrl={raqUrl}
              trackingName={`suite_${suitePrice.suiteCategory}`}
              detailsOnClick={() => {
                setOpenModal({
                  type: "suite",
                  suiteCode: suitePrice.suiteCategory,
                  price: suitePrice.priceFrom,
                  offers: offers,
                  raqCTAUrl: raqUrl
                });
              }}
            />
          );
        })}
      </MosaicLayout>
      {cruiseFareInclusions && cruiseFareInclusions.length > 0 && (
        <CruiseFareInclusionsList
          title={<Trans>Benefits</Trans>}
          subTitle={
            cruiseData.comboType == "World" ? (
              <Trans id="wc-suites-benefits-content">
                Luxurious benefits that ensure you travel in comfort and that
                add an array of enriching cultural events make this
                voyage-of-a-lifetime an even more extraordinary experience.
              </Trans>
            ) : (
              <Trans id="gv-suites-benefits-content">
                Luxurious benefits that ensure you travel in comfort make this
                voyage-of-a-lifetime an even more extraordinary experience.
              </Trans>
            )
          }
          disclaimer={<BlockContent blocks={cruiseFareInclusionsFootnotes} />}
          renderBody={() => (
            <BlockContent blocks={cruiseFareInclusions}></BlockContent>
          )}
        />
      )}
    </SectionContainer>
  );
};

export default React.memo(
  CruiseSectionSuites,
  (prev, next) => prev.cruisePrice == next.cruisePrice
);
